// Personal Access Tokens API - Connected to Backend via API Gateway
// Lets users create/list/revoke PATs for MCP / third-party AI client access.
// See docs/MCP_SERVER.md for the end-to-end MCP connection flow.

import { authenticatedFetch } from './authenticatedFetch';

export interface PersonalAccessToken {
  id: string;
  name: string;
  description: string | null;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

export interface CreatedPersonalAccessToken {
  id: string;
  name: string;
  description: string | null;
  token: string;
  scopes: string[];
  expires_at: string | null;
  created_at: string;
}

export interface CreateTokenRequest {
  name: string;
  description?: string;
  scopes: string[];
  expires_at?: string | null;
}

export interface PatScopeOption {
  value: string;
  label: string;
  helpText: string;
}

// Keep in sync with `AuthService.MCP_VALID_SCOPES` (services/auth/auth_service.py).
export const PAT_SCOPES: PatScopeOption[] = [
  {
    value: 'read',
    label: 'read',
    helpText: 'Read everything — projects, analyses, results, analytics (all services)',
  },
  {
    value: 'write:briefing',
    label: 'write:briefing',
    helpText: 'Create briefing projects/analyses, generate & iterate articles',
  },
  {
    value: 'write:monitoring',
    label: 'write:monitoring',
    helpText: 'Create monitoring projects, trigger crawls',
  },
  {
    value: 'write:similarity',
    label: 'write:similarity',
    helpText: 'Create similarity projects and jobs',
  },
  {
    value: 'write:clustering',
    label: 'write:clustering',
    helpText: 'Create clustering projects and batches',
  },
  {
    value: 'write:wordcloud',
    label: 'write:wordcloud',
    helpText: 'Create wordcloud crawl jobs',
  },
  {
    value: 'write:research',
    label: 'write:research',
    helpText: 'Track / untrack rank-tracked keywords',
  },
];

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return authenticatedFetch<T>(endpoint, { ...options, serviceName: 'Tokens API' });
}

export const tokensAPI = {
  list: () => apiRequest<PersonalAccessToken[]>('/auth/tokens', { method: 'GET' }),

  create: (body: CreateTokenRequest) =>
    apiRequest<CreatedPersonalAccessToken>('/auth/tokens', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  revoke: (id: string) =>
    apiRequest<{ success: boolean }>(`/auth/tokens/${id}`, { method: 'DELETE' }),
};
